// scheman/SchemaView.jsx
import React, { useEffect, useState } from 'react';
import { useSchemaView } from '../hooks/useSchemaView';
import {
  useIsAuthorized,
  useStation,
  useUserInfo,
  useLoading,
  useHideHeader,
  useHideHeaderButton,
  useImpersonatedUserId,
  useLoggedInViaLink
} from '../contexts/GlobalStateContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchWithAuth, getUserInfoById } from '../services/api';
import EditorWrapper from './EditorWrapper';
import VoStationsPanel from './VoStationsPanel';
import BattenburgLoader from '../common/BattenburgLoader';
import DraggableToolbar from '../common/DraggableToolbar';
import { getTodaysDate } from '../services/UtilityServices';
import { useHeartbeat } from '../hooks/useHeartbeat';

const SchemaView = () => {
  const {
    scheduleMetadata,
    isConnected,
    error,
    typingUsers,
    editLocks,
    myLocks,
    refreshScheduleAsync,
    refreshScheduleAllUsersAsync,
    startTyping,
    stopTyping,
    requestEditLock,
    releaseLock,
    saveChanges,
    registerDocumentSavedCallback,
    notifyDocumentSaved 
  } = useSchemaView();

  const { isAuthorized, setIsAuthorized } = useIsAuthorized();
  const { station, setStation } = useStation();
  const { userInfo, setUserInfo } = useUserInfo();
  const { loading, setLoading } = useLoading();
  const { setHideHeader } = useHideHeader();
  const { setHideHeaderButton } = useHideHeaderButton();
  const navigate = useNavigate();
  const location = useLocation();
  const isInfoscreen = new URLSearchParams(location.search).has('station');
  useHeartbeat(isInfoscreen ? 5 * 60 * 1000 : null);

  const isStandard = userInfo?.role === 'Standard';
  const isStandardUser = isStandard;
  const { impersonatedUserId } = useImpersonatedUserId();
  const { loggedInViaLink, setLoggedInViaLink } = useLoggedInViaLink();

  const [availableStationer, setAvailableStationer] = useState([]);
  const [stationChosen, setStationChosen] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [availableVOs, setAvailableVOs] = useState([]);
  const [selectedVoId, setSelectedVoId] = useState('');
  const [selectedVoStations, setSelectedVoStations] = useState(null);
  const [voDate, setVoDate] = useState(getTodaysDate());

  const isSystemAdmin = userInfo?.role === 'SystemAdmin';
  const [preselectedStation, setPreselectedStation] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  // Läs station från URL oavsett hur användaren loggade in
  useEffect(() => {
    if (!isAuthorized || !isStandard) return;

    setStationChosen(false);
    const params = new URLSearchParams(location.search);
    const stationFromUrl = params.get('station');

    if (stationFromUrl) {
      const decodedStation = decodeURIComponent(stationFromUrl);
      setPreselectedStation(decodedStation);
      setStation(decodedStation);
      setLoggedInViaLink(true);
      setHideHeader(true);
      setHideHeaderButton(true);
      setStationChosen(true);
      return;
    }

    // annars laddar vi stationer för dropdown
    setPreselectedStation(null);
    if (station !== '---') setStation("---");
    setSelectedVoStations(null);
    setSelectedVoId('');
    setLoggedInViaLink(false);
    setHideHeader(true);
    setHideHeaderButton(true);
    const loadStations = async () => {
      setLoading(true);
      try {
        const response = await fetchWithAuth('/api/reports/allastationer');
        if (response.ok) {
          let areas = await response.json();
          areas = areas.filter(area => area !== '---');
          setAvailableStationer(areas);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadStations();

    fetchWithAuth('/api/verksamhetsomraden')
      .then(r => r.json())
      .then(setAvailableVOs)
      .catch(() => {});
  }, [location.search, isAuthorized, isStandard]);

  // Ladda initialt schema när station är vald
  useEffect(() => {
    const loadImpersonatedUser = async () => {
      const data = await getUserInfoById(impersonatedUserId);
      setImpersonatedUser(data);
    }

    if (!isAuthorized) return;
    if (isSystemAdmin && impersonatedUserId && !impersonatedUser) {
      loadImpersonatedUser()
    }

    const endStation = preselectedStation || station;

    if (scheduleMetadata?.html === "---" && endStation !== "---") {
      const selectedDate = scheduleMetadata?.currentDate || getTodaysDate();
      refreshSelectedScheduleAsync(selectedDate, endStation);
    }

  }, [station, scheduleMetadata?.html]);

  const resetState = () => {
    if (isStandard) {
      setStation("---");
      setStationChosen(false);
      setPreselectedStation(null);
      setAvailableStationer([]);
      setSelectedVoStations(null);
      setSelectedVoId('');
    }

    setHideHeader(isStandard);
    setHideHeaderButton(isStandard);
    setLoggedInViaLink(false);  // ✅ Rensa flaggan
    setImpersonatedUser(null);
    setLoading(false);

    // Rensa URL-parametrar om det behövs
    const url = new URL(window.location.href);
    if (url.searchParams.has('station')) {
      url.searchParams.delete('station');
      window.history.replaceState({}, '', url);
    }
  }

  const addWeekAndDay = (datum) => {
    if (!datum) return getTodaysDate();

    const date = new Date(datum);

    // Hjälpfunktion för ISO 8601-vecka
    function getISOWeek(d) {
      const dateCopy = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      // Torsdag i den aktuella veckan avgör veckonumret
      const dayNum = dateCopy.getUTCDay() || 7;
      dateCopy.setUTCDate(dateCopy.getUTCDate() + 4 - dayNum);
      const yearStart = new Date(Date.UTC(dateCopy.getUTCFullYear(), 0, 1));
      const weekNo = Math.ceil((((dateCopy - yearStart) / 86400000) + 1) / 7);
      return weekNo;
    }

    // Hämta svensk veckodag
    const weekday = date.toLocaleDateString("sv-SE", { weekday: "long" });
    const week = getISOWeek(date);

    return `${datum} (Vecka ${week}, ${weekday})`;
  }

  const shiftVoDate = (deltaDays) => {
    const [y, m, d] = voDate.split('-').map(Number);
    const newDate = new Date(Date.UTC(y, m - 1, d + deltaDays));
    setVoDate(newDate.toISOString().split('T')[0]);
  };

  const refreshSelectedScheduleAsync = async (date, _station) => {
    if (isStandard) {
      await refreshScheduleAllUsersAsync(date, _station);
    } else {
      await refreshScheduleAsync(date, impersonatedUserId);
    }
  }

  const logOutUserAsync = async () => {
    const response = await fetchWithAuth('/api/authorization/logout', {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error('Logout failed');
    }

    const data = await response.json();

    if (isStandard) {
      navigate('/standard');
    } else {
      navigate("/");
    }

    return data;
  }

  const logOutStandardUserAsync = async () => {
    setLoading(true);
    try {
      const data = await logOutUserAsync();
      if (data.success) {
        resetState()

        setTimeout(() => {
          sessionStorage.clear();
          setUserInfo(null);
          setIsAuthorized(false);
          if (station !== '---') setStation("---");
          navigate("/")
        }, 20);

        return 'userLoggedOut';
      } else {
        alert(`Unexpected server response: ${data.message}`);
        return 'Error';
      }
    } catch (error) {
      console.error(error);
      // Även om servern failar, logga ut lokalt
      sessionStorage.clear();
      setIsAuthorized(false);
      setUserInfo(null);
      if (station !== '---') setStation("---");
      return 'Error';
    } finally {
      setLoading(false);
    }
  }

  const renderStationDropdown = () => {
    if (loading) return <p>Laddar stationer...</p>;
    if (availableStationer.length === 0) return <p>Inga stationer tillgängliga</p>;

    return (
      <select
        value={selectedVoStations ? `vo:${selectedVoId}` : station}
        onChange={(e) => {
          const val = e.target.value;
          if (val.startsWith('vo:')) {
            const voId = val.slice(3);
            const vo = availableVOs.find(v => String(v.id) === voId);
            if (vo) {
              setSelectedVoId(voId);
              setSelectedVoStations(vo.stationer.filter(s => availableStationer.includes(s)));
              setVoDate(getTodaysDate());
              setStation('---');
            }
          } else {
            setSelectedVoStations(null);
            setSelectedVoId('');
            setStation(val);
          }
          setStationChosen(true);
        }}
        className="tw-w-full tw-px-4 tw-py-3 tw-border tw-border-gray-300 tw-rounded-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 tw-text-lg"
      >
        <option value="---">-- Välj station eller VO --</option>
        {availableStationer.map((s) => (
          <option key={s} value={s}>{s}</option>
        ))}
        {availableVOs.length > 0 && (
          <optgroup label="Verksamhetsområden">
            {availableVOs.map(vo => (
              <option key={vo.id} value={`vo:${vo.id}`}>{vo.name}</option>
            ))}
          </optgroup>
        )}
      </select>
    );
  };

  const endStation = preselectedStation || station;

  // Ej inloggad
  if (!isAuthorized) {
    return (
      <div className="tw-container tw-mx-auto">
        <div>
          <div>
            <div className="tw-p-1" />
            <div className="tw-mb-2 tw-w-full">
              <div className="tw-rounded-3xl tw-overflow-hidden tw-border tw-border-blue-500 tw-w-[1700px] tw-max-w-full">
                <div className="tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-text-white tw-px-4 tw-py-3">
                  <h3 className="tw-m-0 tw-text-2xl tw-font-bold tw-text-center">Logga in för att se schemat</h3>
                </div>
                <div className="tw-p-0"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Standard-användare som inte valt station
  if (isStandard && !stationChosen) {
    return (
      <div className="tw-flex tw-items-start tw-justify-center tw-h-screen tw-bg-gray-50 tw-pt-20">
        <div className="tw-w-[330px] tw-bg-white tw-rounded-lg tw-shadow-md tw-p-6">
          {renderStationDropdown()}
          <button
            onClick={async () => logOutStandardUserAsync()}
            className="tw-w-[280px] tw-mt-4 tw-bg-red-500 tw-text-white tw-font-medium tw-rounded-lg hover:tw-bg-red-600 tw-transition-colors"
          >
            Logga ut
          </button>
        </div>
      </div>
    );
  }

  const noScheduleAvailable = endStation === '---' || !scheduleMetadata || scheduleMetadata.html === '---';

  // Huvudvy när station är vald
  return (
    <>
      {error && (
        <div className="schedule-editor-wrapper">
          <div className="tw-absolute tw-top-[5cm] tw-left-1/2 tw--translate-x-1/2 tw-flex tw-items-center tw-gap-4 tw-z-50">
            <h1 className="tw-mt-4 tw-text-4xl tw-text-gray-600 tw-font-bold tw-whitespace-pre-line tw-text-center">
              <span className="tw-text-yellow-800">⚠️ {error}</span>
            </h1>
          </div>
        </div>
      )}

      {(() => {
        // Verksamhetsområde valt — visa alla dess stationer staplade i en gemensam vy
        if (selectedVoStations) {
          return <VoStationsPanel stations={selectedVoStations} date={voDate} />;
        }

        // Ingen station vald
        if (endStation === '---') {
          return (
            <div className="schedule-editor-wrapper">
              <div className="tw-absolute tw-top-[5cm] tw-left-1/2 tw--translate-x-1/2 tw-flex tw-items-center tw-gap-4">
                <h1 className="tw-mt-4 tw-text-4xl tw-text-gray-600 tw-font-bold tw-whitespace-pre-line tw-text-center">
                  Underlag saknas
                </h1>
              </div>
            </div>
          );
        }

        // Station vald men inget schema laddat än
        if (!scheduleMetadata?.html) {
          return (
            <div className="schedule-editor-wrapper">
              <div className="tw-absolute tw-top-[5cm] tw-left-1/2 tw--translate-x-1/2 tw-flex tw-items-center tw-gap-4">
                <h1 className="tw-mt-4 tw-text-4xl tw-text-gray-600 tw-font-bold tw-whitespace-pre-line tw-text-center">
                  Laddar schema för {endStation} - {addWeekAndDay(scheduleMetadata?.currentDate)}
                </h1>
              </div>
            </div>
          );
        }

        if (noScheduleAvailable) {
          return (
            <div className="schedule-editor-wrapper">
              <div className="tw-absolute tw-top-[5cm] tw-left-1/2 tw--translate-x-1/2 tw-flex tw-items-center tw-gap-4">
                <h1 className="tw-mt-4 tw-text-4xl tw-text-gray-600 tw-font-bold tw-whitespace-pre-line tw-text-center">
                  Underlag saknas för {endStation} - {addWeekAndDay(scheduleMetadata?.currentDate)}
                </h1>
              </div>
            </div>
          );
        }

        // Schema finns - visa i ScheduleEditor
        return (
          <EditorWrapper
            originalHtml={scheduleMetadata.html}
            conflicts={scheduleMetadata?.conflicts || []}
            isStandardUser={isStandardUser}
            isEdited={scheduleMetadata.isEdited}
            currentDate={scheduleMetadata.currentDate}
            station={endStation}
            typingUsers={typingUsers}
            editLocks={editLocks}
            myLocks={myLocks}
            onStartTyping={startTyping}
            onStopTyping={stopTyping}
            onRequestLock={requestEditLock}
            onReleaseLock={releaseLock}
            onDocumentSaved={registerDocumentSavedCallback}
            onNotifyDocumentSaved={notifyDocumentSaved}
            key={endStation}
            selectDates={true}
            onDateChange={async (newDate) => {
              setLoading(true);
              await refreshSelectedScheduleAsync(newDate, endStation);
              setLoading(false);
            }}
            onEditModeChange={setIsEditMode}
          />
        );
      })()}

      <div className="tw-bg-transparent tw-flex tw-items-center tw-gap-4 tw-px-4 tw-py-2">
        <div className="tw-min-h-[40px]">
          {loading && (
            <BattenburgLoader width={160} left={'50%'} top={159} />
          )}
        </div>
      </div>

      {/* Datumnavigering, ej för inloggade via länk */}
      {!loggedInViaLink &&
        <DraggableToolbar initialPosition={{ x: window.innerWidth / 2, y: isStandard ? 60 : 100 }}>
          <div className="tw-bg-transparent tw-flex tw-items-center tw-gap-4 tw-px-4 tw-py-2 tw-cursor-grab">
            <button
              onClick={async () => {
                if (selectedVoStations) { shiftVoDate(-1); return; }
                setLoading(true);
                const currentDate = scheduleMetadata?.currentDate || new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });
                const [y, m, d] = currentDate.split('-').map(Number);
                const yesterday = new Date(Date.UTC(y, m - 1, d - 1));
                const prevDate = yesterday.toISOString().split('T')[0];
                await refreshSelectedScheduleAsync(prevDate, station);
                setLoading(false);
              }}
              disabled={selectedVoStations ? loading : (!isConnected || loading || isEditMode)}
              className={`tw-px-4 tw-py-2 tw-rounded-full tw-flex tw-items-center tw-gap-2 tw-font-medium tw-shadow-md tw-transition-colors ${isConnected
                ? 'tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200'
                : 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
                }`}
            >
              <span className="tw-hidden sm:tw-inline">Föregående</span>
            </button>

            <button
              onClick={async () => {
                if (selectedVoStations) { setVoDate(getTodaysDate()); return; }
                setLoading(true);
                await refreshSelectedScheduleAsync(getTodaysDate(), station);
                setLoading(false);
              }}
              disabled={selectedVoStations ? loading : (!isConnected || loading || isEditMode)}
              className={`tw-px-5 tw-py-2 tw-rounded-full tw-font-medium tw-shadow-md tw-transition-colors ${isConnected
                ? 'tw-bg-blue-500 tw-text-white hover:tw-bg-blue-600'
                : 'tw-bg-gray-300 tw-text-gray-500 tw-cursor-not-allowed'
                }`}
            >
              I dag
            </button>

            <button
              onClick={async () => {
                if (selectedVoStations) { shiftVoDate(1); return; }
                setLoading(true);
                const currentDate = scheduleMetadata?.currentDate || new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Stockholm' });
                const [y, m, d] = currentDate.split('-').map(Number);
                const tomorrow = new Date(Date.UTC(y, m - 1, d + 1));
                const nextDate = tomorrow.toISOString().split('T')[0];
                await refreshSelectedScheduleAsync(nextDate, station);
                setLoading(false);
              }}
              disabled={selectedVoStations ? loading : (!isConnected || loading || isEditMode)}
              className={`tw-px-4 tw-py-2 tw-rounded-full tw-flex tw-items-center tw-gap-2 tw-font-medium tw-shadow-md tw-transition-colors ${isConnected
                ? 'tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200'
                : 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
                }`}
            >
              <span className="tw-hidden sm:tw-inline">Nästa</span> →
            </button>

            {isStandard && (
              <div className="tw-px-6">
                <button
                  onClick={async () => logOutStandardUserAsync()}
                  disabled={!isConnected || loading || isEditMode}
                  className={`tw-px-5 tw-py-2 tw-rounded-full tw-font-medium tw-shadow-md tw-transition-colors ${isConnected
                    ? 'tw-bg-red-500 tw-text-white hover:tw-bg-red-600'
                    : 'tw-bg-gray-300 tw-text-gray-500'
                    }`}
                >
                  Logga ut
                </button>
              </div>
            )}
          </div >
        </DraggableToolbar>
      }
    </>
  );
};
export default SchemaView;